import { FC } from 'react'
import styled from 'styled-components'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Icon, IconColor, IconTypes } from '@app/ui'
import { ROUTES } from '@app/constants'
import { guestMode, showSignupPromptModal } from '@app/storage'
import { useSnapshot } from 'valtio'
import { CurrentUserState } from '@app/storage'
import { useTawktoConfig, isVipSubscribeMode } from '@app/hooks/useTawkto'
import { openTawkSupportChat } from '@app/components/SupportChat/supportChatApi'

interface PremiumOverlayProps {
  onSubscribe: () => void;
}

const PremiumOverlay: FC<PremiumOverlayProps> = ({ onSubscribe }) => {
  const { hasPendingSubscription } = useSnapshot(CurrentUserState)
  const { t } = useTranslation()
  const navigate = useNavigate()
  const isGuest = guestMode()
  const tawkConfig = useTawktoConfig()
  const vipMode = tawkConfig ? isVipSubscribeMode(tawkConfig.positions) : false

  const handleSubscribeClick = isGuest
    ? showSignupPromptModal({ message: t('incognito.modal.loginDefaultMessage'), title: t('incognito.modal.loginTitle') })
    : vipMode
      ? openTawkSupportChat
      : onSubscribe

  const subscribeLabel = vipMode
    ? (tawkConfig?.vipSubscribeButtonLabel ?? t('ui.contactToSubscribe'))
    : hasPendingSubscription
      ? t('profile.chooseMethod')
      : t('ui.subscribeToUnlock')

  return (
    <PremiumBlock>
      <LockIconCircle>
        <Icon type={IconTypes.lock} color={IconColor.viloet400} height={20} width={20} />
      </LockIconCircle>
      <PremiumTitle>{t('ui.premiumTip')}</PremiumTitle>
      <PremiumDescription>
        <span>{t('ui.subscribeToViewAllPredictions')}</span>
        <span>{t('ui.analysisAndStreams')}</span>
      </PremiumDescription>
      <PremiumSubscribeButton onClick={handleSubscribeClick}>
        <Icon type={IconTypes.crown} color={IconColor.white} height={16} width={16} />
        {subscribeLabel}
      </PremiumSubscribeButton>
      <PremiumFooter>
        <PremiumFooterLeft>
          <CrownBadge>
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M9.6 8.5L10.5 3.5L7.35 5L6 3.5L4.65 5L1.5 3.5L2.4 8.5H9.6Z" fill="#DBA800" />
            </svg>
          </CrownBadge>
          <PremiumFooterText>
            <span>{t('ui.premiumTipsAreAvailable')}</span>
            <PremiumFooterHighlight>{t('ui.exclusivelyToSubscribers')}</PremiumFooterHighlight>
          </PremiumFooterText>
        </PremiumFooterLeft>
        {isGuest && (
          <PremiumFooterLink onClick={() => navigate(ROUTES.login.short)}>
            {t('ui.alreadyASubscriber')} <span>{t('ui.logIn')}</span>
          </PremiumFooterLink>
        )}
      </PremiumFooter>
    </PremiumBlock>
  )
}

const PremiumBlock = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  border-top: 1px solid #52525B;
  gap: 4px;
  background: #0000004D;
  backdrop-filter: blur(16px);

  [data-theme="light"] & {
    border-top: 1px solid var(--border-primary);
  }
  -webkit-backdrop-filter: blur(4px);
`

const LockIconCircle = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  border: 1.5px solid var(--bg-nonary);
  background: transparent;
`

const PremiumTitle = styled.span`
  font-size: var(--font-size-large);
  font-weight: 700;
  color: var(--text-primary);
`

const PremiumDescription = styled.span`
  font-size: var(--font-size-small);
  color: var(--text-quaternary);
  text-align: center;
  display: flex;
  flex-direction: column;
`

const PremiumSubscribeButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  max-width: 189px;
  background-color: var(--button-primary-color);
  color: var(--text-primary);
  font-size: var(--font-size-normal);
  font-weight: 600;
  padding: 16px 11px;
  border-radius: 8px;
  border: none;
  cursor: pointer;
  margin-top: 8px;
  margin-bottom: 24px;
  &:hover { opacity: 0.9; }
`

const PremiumFooter = styled.div`
  position: absolute;
  bottom: 10px;
  left: 10px;
  right: 10px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: var(--font-size-extra-small);
  padding: 8px 12px;
  background: var(--bg-primary);
  border-radius: 8px;
  gap: 8px;
`

const CrownBadge = styled.div`
  width: 20px;
  height: 20px;
  background: var(--bg-novadecimary);
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`

const PremiumFooterLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  flex: 1;
`

const PremiumFooterText = styled.span`
  color: var(--text-quaternary);
  line-height: 1.4;
  display: flex;
  flex-direction: column;
`

const PremiumFooterHighlight = styled.span`
  color: var(--button-primary-color);
`

const PremiumFooterLink = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  font-size: var(--font-size-extra-small);
  color: var(--text-quaternary);
  white-space: nowrap;
  flex-shrink: 0;
  span {
    color: var(--text-primary);
    text-decoration: underline;
  }
`

export default PremiumOverlay
